import { useState } from 'react';
import { X } from 'lucide-react';
import { useTranslation } from '../../../../lib/i18n';
import { Modal } from '../../../Modal';
import { setRecapVisibleFor } from '../../../../lib/recap-preference';
import { Card } from '../../ui/Card';
import { IconButton } from '../../ui/IconButton';
import styles from './RecapBanner.module.css';

/**
 * The conversation's rolling recap, directly above the composer — what this conversation has been about,
 * for someone returning to a thread they last touched days ago.
 *
 * The × hides the recap on THIS conversation, and the chat header's menu is how it comes back — a control
 * that can only be switched off is a one-way door. Settings keeps the master switch for someone who does
 * not want the line on any thread.
 *
 * Built on the shared Card + IconButton rather than a hand-rolled surface and a bare element — the
 * card-spec / button-spec tests enforce that, and they caught the first version of this file.
 *
 * Clicking it opens the whole recap in the shared `Modal` (whose scrim already carries the DS blur), since
 * the line is clamped to two and the rest of it otherwise has nowhere to go. Mirrors native `RecapOverlay`.
 */
export function RecapBanner({ recap, conversationId }: { recap: string; conversationId: string }) {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);

  return (
    <Card variant="sunken" className={styles.banner}>
      {/* The whole line opens it; the × keeps its own meaning and stops the click reaching here. */}
      <button type="button" className={styles.text} onClick={() => setExpanded(true)} title={t('chat.recap.expand')}>
        <span role="note" aria-label={t('chat.recap.label')}>
          {recap}
        </span>
      </button>
      <IconButton size="small" variant="text" title={t('chat.recap.hide')} aria-label={t('chat.recap.hide')} onClick={() => setRecapVisibleFor(conversationId, false)}>
        <X size={14} strokeWidth={1.5} absoluteStrokeWidth aria-hidden />
      </IconButton>
      <Modal
        open={expanded}
        onClose={() => setExpanded(false)}
        title={t('chat.recap.label')}
        size="minimum"
        maxHeightPx={650}
        scrollBody
      >
        <p className={styles.full}>{recap}</p>
      </Modal>
    </Card>
  );
}
